import { saveAs } from "file-saver";
import { DownloadIcon } from "lucide-react";

import {
  Column,
  ErrorText,
  FileViewer,
  LoadingIndicator,
  Row,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";

import { useGetCompanyFile } from "@/hooks/queries/useGetCompanyFile";

const CompanyFileViewer = ({ companyFileId }: { companyFileId: number }) => {
  const {
    data: companyFile,
    refetch,
    isPending: isCompanyFilePending,
    isError: isCompanyFileError,
  } = useGetCompanyFile({
    companyFileId,
    enabled: true,
  });

  if (isCompanyFilePending) {
    return <LoadingIndicator variant="medium" />;
  } else if (isCompanyFileError) {
    return (
      <Column className="h-full items-center justify-center">
        <ErrorText errorMessage="An error occurred while loading file. Please try again." />
      </Column>
    );
  }

  return (
    <Column className="gap-4">
      <Row className="items-center justify-between">
        <p className="text-lg font-semibold text-slate-700">
          {companyFile.name}
        </p>
        <Button
          className="rounded-full"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async () => {
            const { data } = await refetch();
            if (!data) return;
            saveAs(data.signedUrl, data.name);
          }}
          variant="outline"
        >
          <DownloadIcon className="h-4 w-4" />
          Download
        </Button>
      </Row>
      <FileViewer mimeType={companyFile.mimeType} url={companyFile.signedUrl} />
    </Column>
  );
};

export const CompanyFileViewerDialog = ({
  companyFileId,
  onOpenChange,
  open,
}: {
  companyFileId?: number;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[92vh] overflow-y-scroll">
        {/* This ensures spacing between top of dialog and content. */}
        <DialogHeader />
        {/* The dialog should only be open when `companyFileId` is present. */}
        {companyFileId && <CompanyFileViewer companyFileId={companyFileId} />}
      </DialogContent>
    </Dialog>
  );
};
